import { sendAnalytics } from '@audio-network/analytics'
import { useEffect, useRef } from 'react'
import { useInstantSearch, useStats } from 'react-instantsearch'

import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import { sendEventToSprig } from '@/utils/sendEventToSprig'

import proFacets from '../Filters/ProFilter/ProFacets'
import { SearchUiState } from './SendAnalytics.types'

const parseRange = (rangeValue: string): string[] => rangeValue.split(':')

const hasNoActiveFilters = (state: SearchUiState): boolean => {
	const activeFilters = {
		toggleFilters: Object.keys(state.toggle || {}),
		menuFilters: Object.keys(state.menu || {}),
		rangeFilters: Object.keys(state.range || {}),
		refinementFilters: Object.keys(state.refinementList || {}),
	}

	return (
		activeFilters.toggleFilters.length === 0 &&
		activeFilters.menuFilters.length === 0 &&
		activeFilters.rangeFilters.length === 0 &&
		activeFilters.refinementFilters.length === 0 &&
		!state.query
	)
}

const isDefaultState = (
	state: SearchUiState,
	searchByAudio:
		| {
				service: string
				title: string
				url: string
		  }
		| undefined
		| null,
	searchByPrompt: string | undefined
): boolean => {
	if (searchByAudio || searchByPrompt) {
		return false
	}

	const hasOnlyDefaultSort =
		state.sortBy === 'tracks_recent' && Object.keys(state).length === 1

	return hasNoActiveFilters(state) || hasOnlyDefaultSort
}

const SendAnalytics = (): JSX.Element => {
	const { searchByAudio, searchByPrompt, aiSearchPending, aiSearchResults } =
		useAiSearchStore()
	const { indexUiState, status } = useInstantSearch()
	const { nbHits, page } = useStats()
	const isInitialRender = useRef(true)

	const searchByAudioRef = useRef<{
		service: string
		title: string
		url: string
	}>()

	const searchByPromptRef = useRef<string>()

	useEffect(() => {
		searchByAudioRef.current = searchByAudio
	}, [searchByAudio])

	useEffect(() => {
		searchByPromptRef.current = searchByPrompt
	}, [searchByPrompt])

	useEffect(() => {
		if (isInitialRender.current) {
			isInitialRender.current = false
			return
		}

		// Algolia may briefly report idle with stale unfiltered results before the
		// AI filter is applied — guard until the result count reflects aiSearchResults.
		const aiResultsNotYetApplied =
			!!aiSearchResults && nbHits > aiSearchResults.length

		if (
			status !== 'idle' ||
			aiSearchPending ||
			aiResultsNotYetApplied ||
			isDefaultState(
				indexUiState,
				searchByAudioRef.current,
				searchByPromptRef.current
			)
		) {
			return
		}

		const {
			toggle = {},
			menu = {},
			query: Term,
			sortBy = null,
			range = {},
			refinementList = {},
		} = indexUiState

		const { Duration, TempoStart, TempoFinish, Mix } = range
		const { HasStems, Explicit, Instrumental, Vocal } = toggle
		const { IsMajorKeyMood, KeyStart, KeyFinish, Metre } = menu

		let TempoName = null

		for (const menuItem in menu) {
			if (menuItem.startsWith('TempoName')) {
				TempoName = menu[menuItem]
			}
		}
		const { ['Composers.CollectionSociety']: deselectedProSocieties } =
			refinementList

		let MusicalStyles = null
		let Moods = null
		let Instrumentations = null
		let ProductionGenres = null
		let Toolkit = null

		for (const refinement in refinementList) {
			if (refinement.startsWith('MusicalStyles')) {
				MusicalStyles = refinementList[refinement]
			}
			if (refinement.startsWith('Moods')) {
				Moods = refinementList[refinement]
			}
			if (refinement.startsWith('Instrumentations')) {
				Instrumentations = refinementList[refinement]
			}
			if (refinement.startsWith('ProductionGenres')) {
				ProductionGenres = refinementList[refinement]
			}
			if (refinement.startsWith('Toolkit')) {
				Toolkit = refinementList[refinement]
			}
		}

		const duration = Duration ? parseRange(Duration) : []
		const bpmOpening = TempoStart ? parseRange(TempoStart) : []
		const bpmClosing = TempoFinish ? parseRange(TempoFinish) : []

		const selectedProSocieties = deselectedProSocieties?.length
			? proFacets
					.filter(
						({ value }) =>
							!deselectedProSocieties.some(
								(deselectedPro) => deselectedPro.replace('-', '') === value
							)
					)
					.map(({ value }) => value)
			: null

		let type

		if (useSearchSelectorStore.getState().searchType === 'reference') {
			type = 'Audio Search'
		} else if (useSearchSelectorStore.getState().searchType === 'prompt') {
			type = 'Prompt Search'
		} else if (useSearchSelectorStore.getState().searchType === 'lyrics') {
			type = 'Lyrics'
		} else if (Term) {
			type = 'Keyword'
		}

		const eventParameters = {
			BpmClosingMax: bpmClosing[1],
			BpmClosingMin: bpmClosing[0],
			BpmOpeningMax: bpmOpening[1],
			BpmOpeningMin: bpmOpening[0],
			'Composers.CollectionSociety': selectedProSocieties,
			DurationMax: duration[1],
			DurationMin: duration[0],
			HasStems,
			HideExplicit: Explicit,
			HideMixes: !!Mix,
			Instrumental,
			Instrumentations,
			IsMajorKeyMood,
			KeyFinish,
			KeyStart,
			Metre,
			Moods,
			MusicalStyles,
			Page: page + 1,
			Prompt: searchByPromptRef?.current,
			ProductionGenres,
			ReferencePlatform: searchByAudioRef?.current?.service,
			ReferenceTitle: searchByAudioRef?.current?.title,
			ResultsCount: nbHits,
			SortBy: sortBy ? 'Recent' : 'Relevant',
			TempoName,
			Term,
			Toolkit,
			Type: type,
			Vocal,
		}

		sendEventToSprig('Search Completed')
		sendAnalytics({
			noun: 'Search',
			pastTenseVerb: 'Completed',
			eventParameters: eventParameters,
		})
	}, [indexUiState, nbHits, page, status, aiSearchPending, aiSearchResults])

	return null
}

export default SendAnalytics
